import React, { useState } from 'react';
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Stack, Tooltip, Box, Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import axios from 'axios';
import { API_URL } from '../../config';
import UserEditDialog from './UserEditDialog';

const STATUS_COLOR = {
  pending: 'warning',
  active: 'success',
  suspended: 'error',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const UsersTable = ({ users, onSaved, onViewAs }) => {
  const [editing, setEditing] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  const handleResetSignup = async (u) => {
    const who = u.preferred_name || u.name || u.email;
    const ok = window.confirm(
      `Reset signup for ${who}?\n\n` +
      `Clears their signup answers and reverts status to pending if they were ` +
      `auto-admitted. They'll re-run the signup flow on next login. ` +
      `Name, preferred name, permissions, and entitlements are preserved.`
    );
    if (!ok) return;
    setResettingId(u.user_id);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users/${u.user_id}/reset-signup`,
      );
      if (onSaved) onSaved(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Reset failed';
      window.alert(`Reset signup failed: ${detail}`);
    } finally {
      setResettingId(null);
    }
  };

  if (!users || users.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">No users match this filter.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Permissions</TableCell>
            <TableCell>Entitlements</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.user_id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {u.email}
                </Typography>
              </TableCell>
              <TableCell>{u.preferred_name || u.name || '—'}</TableCell>
              <TableCell>
                <Chip
                  label={u.status}
                  size="small"
                  color={STATUS_COLOR[u.status] || 'default'}
                />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {u.is_admin && (
                    <Chip label="admin" size="small" color="secondary" />
                  )}
                  {(u.permissions || []).filter((p) => p !== 'admin').map((p) => (
                    <Chip key={p} label={p} size="small" />
                  ))}
                  {!u.is_admin && (u.permissions || []).length === 0 && (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                  {(u.entitlements || []).map((e) => (
                    <Chip key={e} label={e} size="small" variant="outlined" />
                  ))}
                  {(u.entitlements || []).length === 0 && (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <Typography variant="caption">{formatDate(u.created_at)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="View as this user">
                  <IconButton size="small" onClick={() => onViewAs(u)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit status / entitlements">
                  <IconButton size="small" onClick={() => setEditing(u)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {u.signup_complete && (
                  <Tooltip title="Reset signup — user re-runs flow on next login">
                    <span>
                      <IconButton
                        size="small"
                        disabled={resettingId === u.user_id}
                        onClick={() => handleResetSignup(u)}
                      >
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <UserEditDialog
        open={!!editing}
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => {
          onSaved && onSaved(updated);
        }}
      />
    </>
  );
};

export default UsersTable;
